import { doc, getDoc, setDoc as firestoreSetDoc, updateDoc, increment } from 'firebase/firestore';
import { db } from '../config/firebase';
import { Subscription, subscriptionTiers } from '../types/subscription';

interface UserData {
  uid: string;
  email: string;
  subscription: Subscription;
  usage: {
    searchCount: number;
    lastSearchDate: Date | null;
  };
}

export class UserService {
  private static instance: UserService;

  private constructor() {}

  /**
   * Creates a new user document in Firestore
   */
  public async setDoc(uid: string, data: UserData): Promise<void> {
    try {
      const userRef = doc(db, 'users', uid);
      await firestoreSetDoc(userRef, {
        ...data,
        subscription: {
          ...data.subscription,
          startDate: data.subscription.startDate.toISOString(),
          endDate: data.subscription.endDate.toISOString()
        },
        usage: {
          ...data.usage,
          lastSearchDate: data.usage.lastSearchDate ? data.usage.lastSearchDate.toISOString() : null
        }
      });
    } catch (error) {
      console.error('Error creating user document:', error);
      throw new Error('Failed to create user document');
    }
  }

  public static getInstance(): UserService {
    if (!UserService.instance) {
      UserService.instance = new UserService();
    }
    return UserService.instance;
  }

  /**
   * Gets user data including subscription and usage information
   */
  public async getUserData(uid: string): Promise<UserData | null> {
    try {
      const docRef = doc(db, 'users', uid);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      const data = docSnap.data() as UserData;
      return {
        ...data,
        subscription: {
          ...data.subscription,
          startDate: new Date(data.subscription.startDate),
          endDate: new Date(data.subscription.endDate)
        },
        usage: {
          ...data.usage,
          lastSearchDate: data.usage.lastSearchDate ? new Date(data.usage.lastSearchDate) : null
        }
      };
    } catch (error) {
      console.error('Error getting user data:', error);
      throw new Error('Failed to get user data');
    }
  }

  /**
   * Checks if the user has an active subscription and hasn't exceeded their search limit
   */
  public async canPerformSearch(uid: string): Promise<{ 
    canSearch: boolean; 
    reason?: string;
    searchesRemaining?: number;
  }> {
    const userData = await this.getUserData(uid);
    
    if (!userData) {
      throw new Error('User data not found');
    }

    if (userData.subscription.status !== 'active') {
      return { 
        canSearch: false, 
        reason: 'Your subscription is not active. Please renew your subscription to continue.' 
      };
    }

    const now = new Date();
    if (userData.subscription.endDate < now) {
      return { 
        canSearch: false, 
        reason: 'Your subscription has expired. Please renew to continue.' 
      };
    }

    const tier = subscriptionTiers.find(t => t.name === userData.subscription.plan);
    if (!tier) {
      throw new Error('Invalid subscription plan');
    }

    const searchesRemaining = tier.searchLimit - userData.usage.searchCount;
    if (searchesRemaining <= 0) {
      return { 
        canSearch: false,
        reason: `You've reached your monthly search limit. Please upgrade your plan for more searches.`,
        searchesRemaining: 0
      };
    }

    return { 
      canSearch: true,
      searchesRemaining 
    };
  }

  /**
   * Increments the user's search count
   */
  public async incrementSearchCount(uid: string): Promise<void> {
    try {
      const userRef = doc(db, 'users', uid);
      await updateDoc(userRef, {
        'usage.searchCount': increment(1),
        'usage.lastSearchDate': new Date()
      });
    } catch (error) {
      console.error('Error incrementing search count:', error);
      throw new Error('Failed to update usage data');
    }
  }

  /**
   * Resets the user's monthly search count
   */
  public async resetSearchCount(uid: string): Promise<void> {
    try {
      const userRef = doc(db, 'users', uid);
      await updateDoc(userRef, {
        'usage.searchCount': 0
      });
    } catch (error) {
      console.error('Error resetting search count:', error);
      throw new Error('Failed to reset usage data');
    }
  }

  /**
   * Updates user's subscription plan
   */
  public async updateSubscription(uid: string, subscription: Subscription): Promise<void> {
    try {
      const userRef = doc(db, 'users', uid);
      await updateDoc(userRef, {
        subscription: {
          ...subscription,
          startDate: subscription.startDate.toISOString(),
          endDate: subscription.endDate.toISOString()
        }
      });
    } catch (error) {
      console.error('Error updating subscription:', error);
      throw new Error('Failed to update subscription');
    }
  }

  /**
   * Sets up enterprise subscription for a user
   */
  public async setEnterpriseSubscription(uid: string): Promise<void> {
    try {
      const userRef = doc(db, 'users', uid);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        // Create user document if it doesn't exist
        const now = new Date();
        const oneYearFromNow = new Date(now.getFullYear() + 1, now.getMonth(), now.getDate());

        await firestoreSetDoc(userRef, {
          uid,
          subscription: {
            plan: 'enterprise',
            status: 'active',
            startDate: now.toISOString(),
            endDate: oneYearFromNow.toISOString()
          },
          usage: {
            searchCount: 0,
            lastSearchDate: null
          }
        });
      } else {
        // Update existing user document
        const now = new Date();
        const oneYearFromNow = new Date(now.getFullYear() + 1, now.getMonth(), now.getDate());

        await updateDoc(userRef, {
          subscription: {
            plan: 'enterprise',
            status: 'active',
            startDate: now.toISOString(),
            endDate: oneYearFromNow.toISOString()
          },
          'usage.searchCount': 0
        });
      }
    } catch (error) {
      console.error('Error setting enterprise subscription:', error);
      throw new Error('Failed to set enterprise subscription');
    }
  }
}

export const userService = UserService.getInstance();
